import { computed, ref } from 'vue';
import { injectIbmktrackScript } from '@/utils/fingerprintTracking';

const REQUEST_TIMEOUT_MS = 5000;

interface GdprConsentResponse {
  success?: string | number;
  error?: string;
}

interface GdprValueResponse {
  gdprSet: boolean;
  value: string; // "0|0|0" format: perf|func|targ, 0=accepted 1=rejected
}

// Module-level singleton state (shared across all consumers)
const performanceCookies = ref(false);
const functionalCookies = ref(false);
const marketingCookies = ref(false);
const hasConsented = ref(false);
const isSubmitting = ref(false);
const hasSubmissionError = ref(false);
const isInitializing = ref(true);
const hasInitializationError = ref(false);
const isReturningVisitor = ref(false);
const isNewVisitor = ref(false);
const consentDialogCount = ref(0);
const isConsentDialogVisible = computed(
  () => consentDialogCount.value > 0 && !isInitializing.value && !hasConsented.value,
);
let initializationPromise: Promise<void> | undefined;

function registerConsentDialog(): () => void {
  consentDialogCount.value++;
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    consentDialogCount.value--;
  };
}

// Set default gtag consent state (GDPR compliance: deny all before user action)
if (typeof window.gtag === 'function') {
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_personalization: 'denied',
    ad_user_data: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

function setConsent() {
  const consentMode: GtagConsentParams = {
    functionality_storage: 'granted',
    security_storage: 'granted',
    ad_storage: marketingCookies.value ? 'granted' : 'denied',
    analytics_storage: performanceCookies.value ? 'granted' : 'denied',
    ad_personalization: functionalCookies.value ? 'granted' : 'denied',
    ad_user_data: marketingCookies.value ? 'granted' : 'denied',
  };

  if (typeof window.gtag === 'function') {
    try {
      window.gtag('consent', 'update', consentMode);
    } catch (err) {
      console.warn('[GDPR] Could not update analytics consent:', err);
    }
  }
  try {
    localStorage.setItem('consentMode', JSON.stringify(consentMode));
  } catch (err) {
    // This cache is optional; the server remains authoritative for saved preferences.
    console.warn('[GDPR] Could not cache consent preferences:', err);
  }
  window.dispatchEvent(new CustomEvent('ibkr:privacy-preferences-updated', { detail: consentMode }));
}

async function submitConsent() {
  isSubmitting.value = true;
  hasSubmissionError.value = false;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch('/response_handlers/cookies/index.php', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        gdpr_update_cookies: 'true',
        perf: String(performanceCookies.value),
        func: String(functionalCookies.value),
        targ: String(marketingCookies.value),
        fp: '',
        gdpr_version: 'B',
      }),
      signal: controller.signal,
    });

    const data: GdprConsentResponse = await res.json();
    clearTimeout(timeout);
    if (res.ok && String(data?.success) === '1' && !data.error) {
      // Trust server confirmation; its cookie need not be visible to JavaScript.
      const perfCookieAccepted = performanceCookies.value;
      await setForecastxCookie(perfCookieAccepted ? 'T' : 'F');
      setConsent();
      if (perfCookieAccepted) {
        injectIbmktrackScript();
      }
      hasConsented.value = true;
    } else {
      hasSubmissionError.value = true;
      console.error('[GDPR] Consent response error:', { status: res.status, data });
    }
  } catch (err) {
    hasSubmissionError.value = true;
    console.error('[GDPR] Consent request failed:', err);
  } finally {
    clearTimeout(timeout);
    isSubmitting.value = false;
  }
}

async function setForecastxCookie(setCookie: 'T' | 'F'): Promise<void> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    await fetch('/response_handler/forecastx/getCookie.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ src: 'forecastx', setCookie }),
      signal: controller.signal,
    });
  } catch {
    // Best effort: the server has already confirmed the primary consent submission.
  } finally {
    clearTimeout(timeout);
  }
}

async function checkPrivacyPreferences() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch('/response_handlers/cookies/get-gdpr-val.php', { signal: controller.signal });
    if (!res.ok) throw new Error('Privacy preference request failed');
    const gdprData: GdprValueResponse = await res.json();
    if (typeof gdprData?.gdprSet !== 'boolean' || (gdprData.gdprSet && !/^[01]\|[01]\|[01]$/.test(gdprData.value))) {
      throw new Error('Invalid privacy preference response');
    }
    if (gdprData.gdprSet) {
      // Parse "0|0|0" format: perf|func|targ, 0=accepted 1=rejected
      const [perf, func, targ] = gdprData.value.split('|');
      performanceCookies.value = perf === '0';
      functionalCookies.value = func === '0';
      marketingCookies.value = targ === '0';
      hasConsented.value = true;
      setConsent();
    }
    isReturningVisitor.value = gdprData.gdprSet;
    isNewVisitor.value = !gdprData.gdprSet;
    hasInitializationError.value = false;
  } catch {
    // Keep consent ungranted and allow callers to retry without classifying the visitor.
    hasInitializationError.value = true;
  } finally {
    clearTimeout(timeout);
  }
}

function init(): Promise<void> {
  // All widgets share one check; a successful result keeps the initial visitor classification stable.
  if (initializationPromise) return initializationPromise;
  // Only the first check blocks rendering; retries must not hide an already-open dialog.
  initializationPromise = checkPrivacyPreferences().finally(() => {
    isInitializing.value = false;
    if (hasInitializationError.value) initializationPromise = undefined;
  });
  return initializationPromise;
}

async function acceptAll() {
  performanceCookies.value = true;
  functionalCookies.value = true;
  marketingCookies.value = true;
  await submitConsent();
}

async function rejectAll() {
  performanceCookies.value = false;
  functionalCookies.value = false;
  marketingCookies.value = false;
  await submitConsent();
}

async function saveCustom() {
  await submitConsent();
}

export const useGdprConsent = () => ({
  // State
  performanceCookies,
  functionalCookies,
  marketingCookies,
  hasConsented,
  isSubmitting,
  hasSubmissionError,
  isInitializing,
  hasInitializationError,
  isReturningVisitor,
  isNewVisitor,
  isConsentDialogVisible,
  // Actions
  registerConsentDialog,
  init,
  acceptAll,
  rejectAll,
  saveCustom,
});
// last modified by AI tools on 2026-10-16
